import React from 'react';
import { Link } from 'react-router-dom';
import Nav from './Nav';
import Footer from './Footer';

function LegalPage({ title, children }) {
  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 20px 80px' }}>
      <Link to="/" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none', display: 'inline-block', marginBottom: 32 }}>
        ← Back
      </Link>
      <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.3px', marginBottom: 6 }}>{title}</h1>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 40 }}>
        Last updated: June 29, 2026
      </p>
      <div style={{ color: 'var(--text)', lineHeight: 1.75, fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}

function H2({ children, id }) {
  return <h2 id={id} style={{ fontSize: 17, fontWeight: 700, margin: '36px 0 10px', letterSpacing: '-.1px' }}>{children}</h2>;
}
function P({ children }) {
  return <p style={{ margin: '0 0 16px', color: 'var(--muted)' }}>{children}</p>;
}
function UL({ children }) {
  return <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: 'var(--muted)' }}>{children}</ul>;
}
function LI({ children }) {
  return <li style={{ marginBottom: 8 }}>{children}</li>;
}

function TermsOfService({ currentUser, currentUserProfile }) {
  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <LegalPage title="Terms of Service">
        <P>
          These Terms of Service ("Terms") govern your use of Your Top 8™ ("we," "us," or "our"),
          an independent service. By creating an account or using Your Top 8, you agree to these Terms.
          If you do not agree, do not use the service.
        </P>

        <H2>The Service</H2>
        <P>
          Your Top 8 is a social profile service that allows users to create a public profile,
          curate a ranked list of up to eight people, and share connection information with others.
          The service is provided free of charge and is currently in active development.
        </P>

        <H2>Eligibility</H2>
        <P>
          You must be at least 18 years old to use Your Top 8. By creating an account, you represent
          that you are 18 or older. Your Top 8 is intended for adults and is not directed to anyone under
          18, and we do not knowingly allow anyone under 18 to create an account.
        </P>

        <H2>Your Account</H2>
        <P>
          You are responsible for maintaining the security of your account. Your Top 8 uses Google
          Sign-In or an email and password for authentication; you are responsible for keeping your
          sign-in credentials secure. You may not transfer your account to another person. Usernames
          are chosen at account creation and cannot be changed.
        </P>

        <H2>Acceptable Use</H2>
        <P>You agree not to use Your Top 8 to:</P>
        <UL>
          <LI>Post content that is illegal, threatening, harassing, defamatory, or obscene.</LI>
          <LI>Impersonate another person or entity.</LI>
          <LI>Add another person's phone number or private contact information to your Top 8 without their knowledge or consent.</LI>
          <LI>Scrape, crawl, or systematically access our service through automated means.</LI>
          <LI>Attempt to gain unauthorized access to any part of the service or its infrastructure.</LI>
          <LI>Use the service in any way that could damage, disable, or impair its operation.</LI>
        </UL>

        <H2>Your Content</H2>
        <P>
          You retain ownership of content you submit to Your Top 8 (profile photos, display names, bios, etc.).
          By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to store,
          display, and distribute that content as necessary to operate the service. You are responsible
          for ensuring you have the right to use any content you submit.
        </P>

        <H2>Public Profiles</H2>
        <P>
          Your profile page is public by default. Your display name, username, profile photo, bio,
          and Top 8 are visible to anyone with the link. Your social links and phone number are
          shown according to the visibility you choose for each one, from public down to private.
          You are responsible for the content you choose to make public.
        </P>

        <H2>Verification and Entity Accounts</H2>
        <P>
          Any account may identify itself as an entity (such as a business, public figure, organization,
          venue, or government body) and select a corresponding category label. A category label is a
          self-declared description and does not, by itself, signify that we have confirmed the account's
          identity.
        </P>
        <P>
          The verified badge, and the ability to display a publicly visible phone number, are granted only
          after we manually review and approve a verification request. We may request documentation,
          control of an official website or social account, or other proof, and we may approve, deny, or
          revoke verification at our sole discretion. Falsely claiming to be a business, public figure, or
          other entity you are not authorized to represent is a violation of these Terms and may result in
          suspension or termination. You may not post a phone number you do not own or control.
        </P>

        <H2>Reporting and Moderation</H2>
        <P>
          You can report profiles, photos, phone numbers, and other content you believe violate these
          Terms. We use a combination of automated and manual review to act on reports. A reported photo
          may be removed from public view immediately pending review; other content is reviewed and may be
          removed or restricted when warranted. We weigh reports by reporter history and may disregard
          reports from accounts that repeatedly file unfounded ones. Verified details may be exempt from
          automatic removal and handled by manual review instead. We may remove content, restrict features,
          or suspend accounts at our sole discretion to keep the service safe.
        </P>

        <H2 id="child-safety-standards">Child Safety Standards</H2>
        <P>
          Your Top 8 prohibits child sexual abuse and exploitation, including content that sexually
          exploits or endangers minors, attempts to solicit minors sexually, shares or requests child
          sexual abuse material, sexualizes minors, or promotes harm to minors. This content is not allowed
          anywhere on the service.
        </P>
        <P>
          Users can report profiles, photos, phone numbers, and other content from public profiles or by
          contacting us at <a href="mailto:legal@yourtop8.com">legal@yourtop8.com</a>. We review reports,
          remove violating content, restrict or terminate accounts when warranted, and may report apparent
          child sexual abuse material or exploitation to law enforcement or the National Center for Missing
          and Exploited Children as required or appropriate.
        </P>

        <H2>Third-Party Links</H2>
        <P>
          Your Top 8 allows you to link to external social media profiles and websites. We are not
          responsible for the content or practices of those third-party sites.
        </P>

        <H2>Service Availability</H2>
        <P>
          We provide Your Top 8 on an "as-is" basis. We do not guarantee uninterrupted availability.
          We reserve the right to modify, suspend, or discontinue any part of the service at any time,
          with or without notice.
        </P>

        <H2>Account Termination</H2>
        <P>
          We reserve the right to suspend or terminate accounts that violate these Terms or that
          we determine, in our sole discretion, are harmful to other users or the service. You may
          delete your account at any time by contacting us.
        </P>

        <H2>Usernames and Identity</H2>
        <P>
          Usernames on Your Top 8 are granted on a first-come, first-served basis and are subject
          to the following conditions:
        </P>
        <UL>
          <LI>
            <strong>No impersonation.</strong> You may not register a username that falsely implies
            affiliation with, endorsement by, or identity as another person, brand, organization,
            or public figure. Accounts found to be impersonating others may be suspended or terminated
            without notice.
          </LI>
          <LI>
            <strong>No username squatting.</strong> Registering usernames with the intent to sell,
            transfer, or hold them for the purpose of profiting from the identity or trademark of
            another party is prohibited. This includes, without limitation, registering the name of
            a public figure, celebrity, sports team, company, or brand with which you have no
            legitimate connection.
          </LI>
          <LI>
            <strong>Reclamation by legitimate parties.</strong> We reserve the right to reclaim any
            username and transfer it to the verified individual, brand, or organization that holds a
            legitimate claim to that name — including but not limited to trademark holders and
            verified public figures. We may charge a one-time administrative fee for this service.
            The original registrant of a reclaimed username is not entitled to compensation.
          </LI>
          <LI>
            <strong>Inactive accounts.</strong> We reserve the right to reclaim usernames associated
            with accounts that have been inactive for an extended period, at our sole discretion.
          </LI>
        </UL>
        <P>
          If you believe a username on Your Top 8 infringes your trademark or misrepresents your identity,
          contact us at <a href="mailto:legal@yourtop8.com">legal@yourtop8.com</a> with documentation
          of your claim.
        </P>

        <H2>Disclaimer of Warranties</H2>
        <P>
          Your Top 8 is provided "as is" and "as available" without warranties of any kind, either
          express or implied. We do not warrant that the service will be error-free, secure, or
          continuously available.
        </P>

        <H2>Limitation of Liability</H2>
        <P>
          To the fullest extent permitted by law, Your Top 8 shall not be liable
          for any indirect, incidental, special, consequential, or punitive damages arising from
          your use of Your Top 8.
        </P>

        <H2>Changes to These Terms</H2>
        <P>
          We may update these Terms from time to time. We will notify you of significant changes
          by updating the "Last updated" date. Continued use of the service after changes constitutes
          acceptance of the updated Terms.
        </P>

        <H2>Governing Law</H2>
        <P>
          These Terms are governed by the laws of the State of Washington, United States,
          without regard to conflict of law principles.
        </P>

        <H2>Contact</H2>
        <P>
          Questions about these Terms?<br />
          Email us at <a href="mailto:legal@yourtop8.com">legal@yourtop8.com</a>
        </P>
      </LegalPage>
      <Footer />
    </>
  );
}

export default TermsOfService;
